import { css, type Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { useCallback, useState } from "react";

import {
  Alert,
  FilterIcon,
  GridViewIcon,
  ListViewIcon,
  PAGE_SIDE_PADDING,
  SearchBox,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { InteractModuleListItem } from "@skilitics/nautilus-endpoint-definition";

import { useFormatCreateModuleAccessDenied } from "../../errors/useFormatAccessDenied";
import { createTagsFilter } from "../filters";
import { useLandingPageSettings } from "../LandingPageSettingsContext";
import { useFetchModuleList } from "../routes/modules/modulePersistence";
import type { Area, View } from "../types";
import { useActiveArea } from "../useActiveArea";
import { FilterWizard } from "../wizards";
import { useFilterSettings } from "../wizards/filter/FilterSettingsContext";
import { AddButton } from "./AddButton";

const Container = styled.div<{ $area: Area }>`
  display: flex;
  align-items: stretch;
  gap: 20px;

  width: 100%;
  height: 95px;

  padding: ${PAGE_SIDE_PADDING}px;
  padding-bottom: 10px;

  opacity: ${({ $area }) => ($area === "modules" ? 1 : 0.5)};

  pointer-events: ${({ $area }) => ($area === "modules" ? "auto" : "none")};

  transition: opacity 300ms ease-in-out;
`;

const SearchContainer = styled.div`
  flex: 1 1 100%;
`;

const sharedCss = ({ theme }: { theme: Theme }) => css`
  display: flex;
  align-items: center;

  width: 45px;

  background-color: ${theme.surface};

  border: ${theme.outlineSoft} solid 1px;
  border-radius: 10px;

  padding: 10px;

  transition:
    color 300ms ease-in-out,
    background-color 300ms ease-in-out;

  cursor: pointer;
`;

const selectedCss = ({ theme }: { theme: Theme }) => css`
  color: ${theme.primary};
  background-color: ${theme.primaryContainer};
`;

const FilterContainer = styled.div`
  ${sharedCss}
`;

const ViewContainer = styled.div`
  display: flex;
  align-items: stretch;
`;

const GridContainer = styled.div`
  ${sharedCss}

  border-inline-end: none;

  border-start-end-radius: 0;
  border-end-end-radius: 0;
`;

const ListContainer = styled.div`
  ${sharedCss}

  border-inline-start: none;

  border-end-start-radius: 0;
  border-start-start-radius: 0;
`;

export const Interactions = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const formatAccessDenied = useFormatCreateModuleAccessDenied();

  const { view, setView, addFilterSet } = useLandingPageSettings();
  const area = useActiveArea();

  const { createAccess } = useFetchModuleList();

  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const { type, tags, reset } = useFilterSettings();

  const handleComplete = useCallback(async () => {
    switch (type) {
      case "tags": {
        if (tags.length) {
          addFilterSet({ id: "tags", filters: [createTagsFilter(tags)], operator: "and", metadata: tags });
        }
        break;
      }
    }

    setOpen(false);
    reset();
  }, [addFilterSet, reset, tags, type]);

  const createViewSelect = useCallback(
    (selectedView: View) => () => {
      if (view === selectedView) return;

      setView(selectedView);
      //setOrdering("updatedOn");
      //setOrderingDirection("ascending");
    },
    [setView, view],
  );

  return (
    <>
      <Container $area={area} data-test="interactions-container">
        <AddButton />

        <SearchContainer data-test="search-container">
          <SearchBox<InteractModuleListItem> onlyKeys={["title", "learningObjective"]} />
        </SearchContainer>

        <Tooltip title={formatMessage("filters")}>
          <FilterContainer
            onClick={handleClick}
            css={open ? (theme) => selectedCss({ theme }) : undefined}
            data-test="filter-container"
          >
            <FilterIcon />
          </FilterContainer>
        </Tooltip>

        <ViewContainer>
          <Tooltip title={formatMessage("gridView")}>
            <GridContainer
              onClick={createViewSelect("grid")}
              css={view === "grid" ? (theme) => selectedCss({ theme }) : undefined}
              data-test="grid-container"
            >
              <GridViewIcon />
            </GridContainer>
          </Tooltip>
          <Tooltip title={formatMessage("listView")}>
            <ListContainer
              onClick={createViewSelect("list")}
              css={view === "list" ? (theme) => selectedCss({ theme }) : undefined}
              data-test="list-container"
            >
              <ListViewIcon />
            </ListContainer>
          </Tooltip>
        </ViewContainer>
      </Container>

      {createAccess?.denyCause && <Alert severity="warning">{formatAccessDenied(createAccess.denyCause)}</Alert>}
      <FilterWizard
        open={open}
        onCancel={handleClose}
        onClose={handleClose}
        onComplete={handleComplete}
        contentStyle={{ height: "380px" }}
        stepperStyle={{ padding: "0 45px 52px 45px" }}
      />
    </>
  );
};

const formatStrings = {
  filters: {
    en: "Filters",
  },
  gridView: {
    en: "Grid View",
  },
  listView: {
    en: "List View",
  },
} satisfies FormatMessages;
